import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar, ArrowRight, Loader2, Search, X, ChevronLeft, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const NewsList = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const currentPage = parseInt(searchParams.get('page') || '1', 10);
    const qFromUrl = searchParams.get('q') || '';
    const [searchTerm, setSearchTerm] = useState(qFromUrl);
    const limit = 9; // 3 kolom x 3 baris

    // Sinkronisasi input dengan URL query jika user navigasi Back/Forward
    useEffect(() => {
        setSearchTerm(searchParams.get('q') || '');
    }, [searchParams]);

    useEffect(() => {
        fetchNews();
    }, [currentPage, qFromUrl]);

    const fetchNews = async () => {
        try {
            setLoading(true);
            const params = {
                is_published: true,
                page: currentPage,
                limit: limit
            };
            if (qFromUrl.trim()) {
                params.search = qFromUrl.trim();
            }

            const response = await axios.get(`${API_URL}/news`, { params });
            const data = response.data;

            if (Array.isArray(data)) {
                setNews(data);
                setTotal(data.length);
                setTotalPages(Math.ceil(data.length / limit) || 1);
            } else {
                setNews(data.news || []);
                setTotal(data.total || 0);
                setTotalPages(data.pages || 1);
            }
        } catch (error) {
            console.error('Error fetching news:', error);
            setNews([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        const nextParams = new URLSearchParams(searchParams);
        if (searchTerm.trim()) {
            nextParams.set('q', searchTerm.trim());
        } else {
            nextParams.delete('q');
        }
        nextParams.set('page', '1');
        setSearchParams(nextParams);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('q');
        nextParams.set('page', '1');
        setSearchParams(nextParams);
    };

    const handlePageChange = (newPage) => {
        if (newPage < 1 || newPage > totalPages || newPage === currentPage) return;
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('page', newPage.toString());
        setSearchParams(nextParams);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getPaginationRange = () => {
        const delta = 1;
        const range = [];
        const rangeWithDots = [];

        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
                range.push(i);
            }
        }

        let prev = 0;
        for (const i of range) {
            if (prev) {
                if (i - prev === 2) {
                    rangeWithDots.push(prev + 1);
                } else if (i - prev !== 1) {
                    rangeWithDots.push('...');
                }
            }
            rangeWithDots.push(i);
            prev = i;
        }

        return rangeWithDots;
    };

    const startItem = total === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endItem = Math.min(currentPage * limit, total);

    return (
        <div className="bg-emerald-50/30 min-h-screen py-16 sm:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl sm:text-5xl font-display font-medium text-emerald-950 mb-4">Berita & Informasi</h1>
                    <p className="text-lg text-emerald-700/80 max-w-2xl mx-auto mb-8">
                        Ikuti perkembangan terbaru, artikel, dan pengumuman dari Spencer Green Hotel.
                    </p>

                    {/* Search Bar */}
                    <form onSubmit={handleSearchSubmit} className="max-w-md mx-auto relative flex items-center">
                        <div className="relative w-full">
                            <Search className="w-5 h-5 text-emerald-600 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Cari judul atau topik artikel..."
                                className="w-full pl-12 pr-10 py-3 rounded-full bg-white border border-emerald-200/80 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm text-gray-800 placeholder-gray-400 transition-all"
                            />
                            {searchTerm && (
                                <button
                                    type="button"
                                    onClick={handleClearSearch}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded-full"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </form>
                </div>

                {/* Subheader / Info Bar */}
                {!loading && total > 0 && (
                    <div className="flex flex-col sm:flex-row justify-between items-center mb-8 pb-4 border-b border-emerald-100 text-sm text-emerald-800">
                        <span>
                            Menampilkan <strong className="font-semibold text-emerald-950">{startItem}–{endItem}</strong> dari <strong className="font-semibold text-emerald-950">{total}</strong> artikel
                            {qFromUrl && (
                                <span className="ml-1 text-gray-500">untuk pencarian "{qFromUrl}"</span>
                            )}
                        </span>
                        <span className="text-xs text-emerald-600 mt-1 sm:mt-0">Halaman {currentPage} dari {totalPages}</span>
                    </div>
                )}

                {/* Content */}
                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <Loader2 className="w-10 h-10 animate-spin text-emerald-600" />
                    </div>
                ) : news.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-2xl border border-emerald-100 p-8 max-w-lg mx-auto">
                        <p className="text-xl text-emerald-900 font-medium mb-2">Tidak ada artikel ditemukan</p>
                        <p className="text-sm text-gray-500 mb-6">
                            {qFromUrl ? `Tidak ditemukan artikel dengan kata kunci "${qFromUrl}".` : 'Belum ada berita yang diterbitkan saat ini.'}
                        </p>
                        {qFromUrl && (
                            <button
                                onClick={handleClearSearch}
                                className="px-5 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors"
                            >
                                Reset Pencarian
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {news.map((post) => (
                                <Link 
                                    to={`/news/${post.slug}`} 
                                    key={post.news_id}
                                    className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col border border-emerald-50"
                                >
                                    <div className="aspect-[4/3] overflow-hidden bg-gray-100 relative">
                                        {post.image_url ? (
                                            <img 
                                                src={post.image_url} 
                                                alt={post.title} 
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-400 bg-emerald-50">
                                                <span className="font-display text-emerald-900/20 text-4xl">News</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-6 flex flex-col flex-1">
                                        <div className="flex items-center gap-2 text-sm text-emerald-600 mb-3">
                                            <Calendar className="w-4 h-4" />
                                            <span>{new Date(post.created_at).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                                        </div>
                                        <h3 className="text-xl font-display font-medium text-gray-900 mb-3 group-hover:text-emerald-600 transition-colors line-clamp-2">
                                            {post.title}
                                        </h3>
                                        <p className="text-gray-600 line-clamp-3 mb-6 flex-1">
                                            {post.excerpt}
                                        </p>
                                        <div className="flex items-center text-emerald-600 font-medium group-hover:gap-2 transition-all">
                                            <span>Baca Selengkapnya</span>
                                            <ArrowRight className="w-4 h-4 ml-1" />
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>

                        {/* Pagination Navigation */}
                        {totalPages > 1 && (
                            <div className="mt-16 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-emerald-100 pt-8">
                                <p className="text-sm text-gray-500 order-2 sm:order-1">
                                    Halaman <span className="font-semibold text-emerald-900">{currentPage}</span> dari <span className="font-semibold text-emerald-900">{totalPages}</span>
                                </p>

                                <div className="flex items-center gap-1.5 order-1 sm:order-2">
                                    {/* Previous Button */}
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage <= 1}
                                        aria-label="Halaman sebelumnya"
                                        className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-emerald-200 text-sm font-medium text-emerald-800 bg-white hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                        <span className="hidden sm:inline">Sebelumnya</span>
                                    </button>

                                    {/* Page Number Buttons */}
                                    <div className="flex items-center gap-1">
                                        {getPaginationRange().map((pageItem, index) => {
                                            if (pageItem === '...') {
                                                return (
                                                    <span key={`dots-${index}`} className="px-2 py-1 text-gray-400 text-sm">
                                                        ...
                                                    </span>
                                                );
                                            }
                                            const isCurrent = pageItem === currentPage;
                                            return (
                                                <button
                                                    key={`page-${pageItem}`}
                                                    onClick={() => handlePageChange(pageItem)}
                                                    className={`min-w-[36px] h-9 px-3 rounded-lg text-sm font-medium transition-all ${
                                                        isCurrent
                                                            ? 'bg-emerald-600 text-white shadow-sm'
                                                            : 'bg-white border border-emerald-200 text-emerald-800 hover:bg-emerald-50'
                                                    }`}
                                                >
                                                    {pageItem}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* Next Button */}
                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage >= totalPages}
                                        aria-label="Halaman selanjutnya"
                                        className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-emerald-200 text-sm font-medium text-emerald-800 bg-white hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <span className="hidden sm:inline">Selanjutnya</span>
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default NewsList;
